import React, { useEffect, useState } from "react";
import api, { formatIDR } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, Pencil, Trash2, Lock, Banknote, ChevronDown, ChevronUp, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const emptyLoan = { technician_id: "", loan_date: "", amount: 0, notes: "", disburse_method: "", disburse_date: "", disburse_admin: "", disburse_account_index: "", notify_wa: true };
const emptyPay = { date: "", amount: 0 };

const todayStr = () => new Date().toISOString().slice(0, 10);

const Kasbon = () => {
  const { user } = useAuth();
  const [locked, setLocked] = useState(true);
  const [lockPwd, setLockPwd] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [list, setList] = useState([]);
  const [techs, setTechs] = useState([]);
  const [filterTech, setFilterTech] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyLoan);
  const [editingId, setEditingId] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm] = useState(emptyPay);
  const [confirmDel, setConfirmDel] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const techName = (id) => techs.find((t) => t.id === id)?.name || "—";

  const load = async () => {
    const [k, t] = await Promise.all([
      api.get("/kasbon", { params: filterTech ? { technician_id: filterTech } : {} }),
      api.get("/technicians"),
    ]);
    setList(k.data);
    setTechs(t.data);
  };

  useEffect(() => {
    if (!locked) load();
    // eslint-disable-next-line
  }, [locked, filterTech]);

  const submitUnlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    try {
      await api.post("/settings/unlock", { password: lockPwd });
      setLocked(false);
      setLockPwd("");
      toast.success("Akses kasbon terbuka");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password salah");
    } finally {
      setUnlocking(false);
    }
  };

  const openCreate = () => { setForm({ ...emptyLoan, loan_date: todayStr(), disburse_date: todayStr(), disburse_admin: user?.name || "" }); setEditingId(null); setModal(true); };
  const openEdit = (k) => {
    setForm({ technician_id: k.technician_id, loan_date: k.loan_date, amount: k.amount, notes: k.notes || "", disburse_method: k.disburse_method || "", disburse_date: k.disburse_date || k.loan_date, disburse_admin: k.disburse_admin || user?.name || "", disburse_account_index: k.disburse_account ? String(k.disburse_account.index ?? "") : "", notify_wa: false });
    setEditingId(k.id); setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: Number(form.amount) || 0, disburse_account_index: form.disburse_method === "transfer" && form.disburse_account_index !== "" ? Number(form.disburse_account_index) : null };
    if (!payload.technician_id) { toast.error("Pilih teknisi"); return; }
    if (!editingId && !payload.disburse_method) { toast.error("Pilih diberikan via Cash atau Transfer"); return; }
    try {
      if (editingId) {
        await api.put(`/kasbon/${editingId}`, payload);
        toast.success("Kasbon diperbarui");
      } else {
        const { data: k } = await api.post("/kasbon", payload);
        toast.success("Kasbon ditambahkan");
        if (k.wa_notify) {
          if (k.wa_notify.ok) toast.success("Info kasbon ke karyawan masuk antrian WA");
          else toast.warning("WA ke karyawan tidak terkirim: " + (k.wa_notify.error || "-"));
        }
      }
      setModal(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan kasbon");
    }
  };

  const submitPay = async (e) => {
    e.preventDefault();
    const amount = Number(payForm.amount) || 0;
    if (amount <= 0) { toast.error("Jumlah pembayaran harus lebih dari 0"); return; }
    if (amount > payModal.remaining) { toast.error(`Melebihi sisa pinjaman (${formatIDR(payModal.remaining)})`); return; }
    try {
      await api.post(`/kasbon/${payModal.id}/payments`, { date: payForm.date, amount });
      toast.success("Pembayaran dicatat");
      setPayModal(null); setPayForm(emptyPay); load();
    } catch {
      toast.error("Gagal mencatat pembayaran");
    }
  };

  const payrollMonthOf = (p) => p.payroll_month || ((p.note || "").startsWith("Potongan gaji ") ? p.note.slice(14).trim() : "");

  const removePayment = async (kasbonId, p) => {
    const pm = payrollMonthOf(p);
    if (pm && !window.confirm(`Pembayaran ini berasal dari potongan payroll ${pm}. Jika dihapus, potongan kasbon di payroll bulan tersebut ikut berkurang ${formatIDR(p.amount)}. Lanjutkan?`)) return;
    try {
      await api.delete(`/kasbon/${kasbonId}/payments/${p.id}`);
      toast.success(pm ? `Pembayaran dihapus — potongan payroll ${pm} diperbarui` : "Pembayaran dihapus");
      load();
    } catch {
      toast.error("Gagal menghapus pembayaran");
    }
  };

  const remove = async () => {
    if (!confirmDel) return;
    await api.delete(`/kasbon/${confirmDel.id}`);
    setConfirmDel(null);
    toast.success("Kasbon dihapus");
    load();
  };

  const totalRemaining = list.reduce((s, k) => s + (k.remaining || 0), 0);

  if (locked) {
    return (
      <Layout>
        <div className="max-w-md mx-auto mt-12 bg-white border border-[#E4E4E7]" data-testid="kasbon-lock-screen">
          <div className="px-6 py-4 border-b border-[#E4E4E7]">
            <div className="label-cap">Akses Terproteksi</div>
            <h2 className="font-heading text-2xl font-bold">Kasbon Karyawan</h2>
          </div>
          <form onSubmit={submitUnlock} className="p-6 space-y-4">
            <p className="text-sm text-[#52525B]">Halaman ini diproteksi. Masukkan password pengaturan untuk mengelola kasbon karyawan.</p>
            <input
              type="password"
              required
              autoFocus
              value={lockPwd}
              onChange={(e) => setLockPwd(e.target.value)}
              placeholder="Password pengaturan"
              data-testid="kasbon-unlock-input"
              className="w-full px-3 py-2.5 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono"
            />
            <button type="submit" disabled={unlocking} data-testid="kasbon-unlock-button" className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60">
              <Lock size={14} /> {unlocking ? "Memverifikasi…" : "Buka Kasbon"}
            </button>
          </form>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div>
          <div className="label-cap mb-2">Keuangan Karyawan</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Kasbon Karyawan</h1>
        </div>
        <button onClick={openCreate} data-testid="add-kasbon-button" className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
          <Plus size={14} /> Tambah Kasbon
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6 max-w-xl">
        <div className="bg-white border border-[#E4E4E7] p-4">
          <div className="label-cap mb-1">Total Sisa Pinjaman</div>
          <div className="font-mono font-bold text-xl text-[#DC2626]" data-testid="kasbon-total-remaining">{formatIDR(totalRemaining)}</div>
        </div>
        <div className="bg-white border border-[#E4E4E7] p-4">
          <div className="label-cap mb-1">Kasbon Berjalan</div>
          <div className="font-mono font-bold text-xl" data-testid="kasbon-active-count">{list.filter((k) => k.status === "berjalan").length}</div>
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7]">
        <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center gap-2">
          <Banknote size={14} className="text-[#52525B]" />
          <select value={filterTech} onChange={(e) => setFilterTech(e.target.value)} data-testid="kasbon-filter-tech" className="text-sm bg-transparent outline-none">
            <option value="">Semua Karyawan</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="border-b border-[#E4E4E7]">
                <th className="text-left label-cap px-4 py-3">Karyawan</th>
                <th className="text-left label-cap px-4 py-3">Tgl Pinjam</th>
                <th className="text-right label-cap px-4 py-3">Jumlah</th>
                <th className="text-right label-cap px-4 py-3">Dibayar</th>
                <th className="text-right label-cap px-4 py-3">Sisa</th>
                <th className="text-left label-cap px-4 py-3">Status</th>
                <th className="text-right label-cap px-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-sm text-[#52525B]">Belum ada kasbon.</td></tr>
              )}
              {list.map((k) => (
                <React.Fragment key={k.id}>
                  <tr className="border-b border-[#E4E4E7] row-hover" data-testid={`kasbon-row-${k.id}`}>
                    <td className="px-4 py-3 text-sm font-bold">{techName(k.technician_id)}</td>
                    <td className="px-4 py-3 text-sm font-mono">{k.loan_date}
                      {k.disburse_method && (
                        <div className="text-[10px] font-sans text-[#52525B]" data-testid={`kasbon-disburse-${k.id}`}>
                          {k.disburse_method === "cash"
                            ? `Cash · ${k.disburse_date}${k.disburse_admin ? " · oleh " + k.disburse_admin : ""}`
                            : `Transfer · ${k.disburse_date} · ${k.disburse_account?.bank || ""} ${k.disburse_account?.number || ""}`}
                          {k.wa_notify && <span className={k.wa_notify.ok ? "text-[#059669]" : "text-[#DC2626]"}>{k.wa_notify.ok ? " · WA ✓" : " · WA gagal"}</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-right">{formatIDR(k.amount)}</td>
                    <td className="px-4 py-3 text-sm font-mono text-right text-[#059669]">{formatIDR(k.paid_total)}</td>
                    <td className="px-4 py-3 text-sm font-mono text-right font-bold" data-testid={`kasbon-remaining-${k.id}`}>{formatIDR(k.remaining)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${k.status === "lunas" ? "bg-[#059669]/10 text-[#059669]" : "bg-[#D97706]/10 text-[#D97706]"}`} data-testid={`kasbon-status-${k.id}`}>
                        {k.status === "lunas" ? "Lunas" : "Berjalan"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {k.status !== "lunas" && (
                          <button onClick={() => { setPayModal(k); setPayForm({ date: todayStr(), amount: "" }); }} title="Bayar cicilan" data-testid={`kasbon-pay-${k.id}`} className="px-2 py-1.5 text-[10px] font-bold uppercase hover:bg-[#059669]/10 text-[#059669] border border-[#E4E4E7]">Bayar</button>
                        )}
                        <button onClick={() => setExpanded(expanded === k.id ? null : k.id)} title="Riwayat pembayaran" data-testid={`kasbon-expand-${k.id}`} className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7]">
                          {expanded === k.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                        <button onClick={() => openEdit(k)} title="Edit" data-testid={`kasbon-edit-${k.id}`} className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7]"><Pencil size={14} /></button>
                        <button onClick={() => setConfirmDel(k)} title="Hapus" data-testid={`kasbon-delete-${k.id}`} className="p-2 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                  {expanded === k.id && (
                    <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                      <td colSpan="7" className="px-6 py-3">
                        <div className="label-cap mb-2">Riwayat Pembayaran {k.notes ? `— Catatan: ${k.notes}` : ""}</div>
                        {(k.payments || []).length === 0 ? (
                          <div className="text-sm text-[#52525B]">Belum ada pembayaran.</div>
                        ) : (
                          <div className="space-y-1">
                            {k.payments.map((p) => (
                              <div key={p.id} className="flex items-center gap-4 text-sm" data-testid={`kasbon-payment-${p.id}`}>
                                <span className="font-mono w-28">{p.date}</span>
                                <span className="font-mono font-bold text-[#059669]">{formatIDR(p.amount)}</span>
                                {payrollMonthOf(p) && <span className="text-xs text-[#52525B]">Potongan payroll {payrollMonthOf(p)}</span>}
                                <button onClick={() => removePayment(k.id, p)} title="Hapus pembayaran" data-testid={`kasbon-payment-delete-${p.id}`} className="p-1 text-[#DC2626] hover:bg-[#DC2626]/10"><X size={12} /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-[#E4E4E7] w-full max-w-lg" data-testid="kasbon-form">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <div className="label-cap">{editingId ? "Ubah" : "Tambah"} Data</div>
              <h2 className="font-heading text-2xl font-bold">Kasbon</h2>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="label-cap block mb-1.5">Karyawan<span className="text-[#DC2626]">*</span></label>
                <select required data-testid="kasbon-tech-select" value={form.technician_id} onChange={(e) => setForm({ ...form, technician_id: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] bg-white">
                  <option value="">— Pilih Karyawan —</option>
                  {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-cap block mb-1.5">Tanggal Peminjaman<span className="text-[#DC2626]">*</span></label>
                <input type="date" required data-testid="kasbon-date-input" value={form.loan_date} onChange={(e) => setForm({ ...form, loan_date: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" />
              </div>
              <div>
                <label className="label-cap block mb-1.5">Jumlah Uang (IDR)<span className="text-[#DC2626]">*</span></label>
                <input type="number" required min="1" data-testid="kasbon-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" placeholder="500000" />
              </div>
              <div className="col-span-2">
                <label className="label-cap block mb-1.5">Diberikan Via{!editingId && <span className="text-[#DC2626]">*</span>}</label>
                <div className="flex gap-2">
                  {[["cash", "Cash"], ["transfer", "Transfer"]].map(([m, l]) => (
                    <button type="button" key={m} onClick={() => setForm({ ...form, disburse_method: m })} data-testid={`kasbon-method-${m}`}
                      className={`flex-1 px-3 py-2 text-xs font-bold uppercase border ${form.disburse_method === m ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-[#E4E4E7] bg-white"}`}>{l}</button>
                  ))}
                </div>
              </div>
              {form.disburse_method && (
                <div>
                  <label className="label-cap block mb-1.5">{form.disburse_method === "cash" ? "Tanggal Diberikan" : "Tanggal Transfer"}<span className="text-[#DC2626]">*</span></label>
                  <input type="date" required data-testid="kasbon-disburse-date" value={form.disburse_date} onChange={(e) => setForm({ ...form, disburse_date: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" />
                </div>
              )}
              {form.disburse_method === "cash" && (
                <div>
                  <label className="label-cap block mb-1.5">Admin yang Memberikan<span className="text-[#DC2626]">*</span></label>
                  <input required data-testid="kasbon-disburse-admin" value={form.disburse_admin} onChange={(e) => setForm({ ...form, disburse_admin: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]" placeholder="Nama admin" />
                </div>
              )}
              {form.disburse_method === "transfer" && (() => {
                const accs = (techs.find((t) => t.id === form.technician_id)?.bank_accounts) || [];
                return (
                  <div>
                    <label className="label-cap block mb-1.5">Ke Rekening Karyawan<span className="text-[#DC2626]">*</span></label>
                    <select required data-testid="kasbon-disburse-account" value={form.disburse_account_index} onChange={(e) => setForm({ ...form, disburse_account_index: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] bg-white">
                      <option value="">— Pilih rekening —</option>
                      {accs.map((a, i) => <option key={i} value={String(i)}>{a.bank} · {a.number}{a.holder ? ` a.n. ${a.holder}` : ""}</option>)}
                    </select>
                    {form.technician_id && accs.length === 0 && <p className="text-[10px] text-[#DC2626] mt-1">Karyawan ini belum punya data rekening — isi dulu di Pengaturan → Karyawan.</p>}
                  </div>
                );
              })()}
              <div className="col-span-2">
                <label className="label-cap block mb-1.5">Catatan</label>
                <input data-testid="kasbon-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]" placeholder="Keperluan pinjaman…" />
              </div>
            </div>
            {!editingId && (
              <label className="mx-6 mb-4 flex items-start gap-2 border border-[#E4E4E7] px-3 py-2 cursor-pointer text-xs">
                <input type="checkbox" data-testid="kasbon-notify-wa" checked={!!form.notify_wa} onChange={(e) => setForm({ ...form, notify_wa: e.target.checked })} className="mt-0.5 accent-[#16A34A]" />
                <span><b>Kirim info WA ke karyawan</b><span className="block text-[10px] text-[#52525B]">Pesan natural (jumlah, cash/transfer, tanggal) lewat antrian WA otomatis.</span></span>
              </label>
            )}
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-end gap-2">
              <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button type="submit" data-testid="kasbon-submit-button" className="px-4 py-2 text-xs font-bold uppercase bg-[#002FA7] text-white hover:bg-[#00227A]">Simpan</button>
            </div>
          </form>
        </div>
      )}

      {payModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setPayModal(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitPay} className="bg-white border border-[#E4E4E7] w-full max-w-md" data-testid="kasbon-pay-form">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <div className="label-cap">Bayar Cicilan</div>
              <h2 className="font-heading text-2xl font-bold">{techName(payModal.technician_id)}</h2>
              <p className="text-xs text-[#52525B] mt-1">Sisa pinjaman: <span className="font-mono font-bold text-[#DC2626]">{formatIDR(payModal.remaining)}</span></p>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div>
                <label className="label-cap block mb-1.5">Tanggal Pembayaran<span className="text-[#DC2626]">*</span></label>
                <input type="date" required data-testid="kasbon-pay-date-input" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" />
              </div>
              <div>
                <label className="label-cap block mb-1.5">Jumlah (IDR)<span className="text-[#DC2626]">*</span></label>
                <input type="number" required min="1" data-testid="kasbon-pay-amount-input" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" placeholder="100000" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-end gap-2">
              <button type="button" onClick={() => setPayModal(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button type="submit" data-testid="kasbon-pay-submit-button" className="px-4 py-2 text-xs font-bold uppercase bg-[#059669] text-white hover:bg-[#047857]">Catat Pembayaran</button>
            </div>
          </form>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-sm w-full p-6">
            <h3 className="font-heading text-lg font-bold mb-2">Hapus Kasbon?</h3>
            <p className="text-sm text-[#52525B] mb-6">Kasbon {techName(confirmDel.technician_id)} sebesar {formatIDR(confirmDel.amount)} beserta riwayat pembayarannya akan dihapus permanen.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={remove} data-testid="confirm-delete-kasbon-button" className="px-4 py-2 text-xs font-bold uppercase bg-[#DC2626] text-white">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Kasbon;
